import React, { createContext, useContext, useState, useEffect } from 'react'
import { cartLineKey, mergeCartRows, addLine, removeOrderedLines } from './cartMath'

const CartContext = createContext()

export function useCart() {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

// 初始化时同步读取 localStorage。
// 不能改成"先给空数组、再用 useEffect 回填"——那样首次渲染后的保存 effect 会先把
// 空数组写回 localStorage，覆盖掉真实数据（StrictMode 双调用 effect 时必然丢购物车，
// 生产环境也存在一个短暂的竞态窗口）。
function loadCartFromStorage() {
  try {
    const savedCart = localStorage.getItem('cart')
    // mergeCartRows 会顺便把历史脏数据（重复行、旧 key 格式）收敛掉
    return mergeCartRows(savedCart ? JSON.parse(savedCart) : [])
  } catch (error) {
    console.error('Error loading cart from localStorage:', error)
    localStorage.removeItem('cart')
    return []
  }
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState(loadCartFromStorage)

  // 保存购物车数据到localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems))
  }, [cartItems])

  // 添加商品到购物车
  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => addLine(prevItems, {
      _id: product._id || product.id,
      productId: product.productId,
      name: product.name?.en || product.name,
      price: product.price || product.selectedPrice || 0,
      image: product.images && product.images.length > 0 ? product.images[0] : product.image || null,
      images: product.images || [],
      size: product.selectedSize || null,
      sku: product.selectedSku || null
    }, quantity))
  }

  // 更新商品数量
  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(id)
      return
    }
    
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === id ? { ...item, quantity: newQuantity } : item
      )
    )
  }

  // 从购物车移除商品
  const removeFromCart = (id) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== id))
  }

  // 下单成功后移除已购买的那些购物车行（orderedLines 传购物车行 id，见 cartMath）
  const removeOrderItems = (orderedLines) => {
    setCartItems(prevItems => removeOrderedLines(prevItems, orderedLines))
  }

  // 清空购物车
  const clearCart = () => {
    setCartItems([])
  }

  // 获取购物车商品总数
  const getTotalItems = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0)
  }

  // 获取购物车总价
  const getTotalPrice = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  }

  // 检查商品是否在购物车中
  const isInCart = (id) => {
    return cartItems.some(item => item.id === id)
  }

  // 获取商品在购物车中的数量
  const getItemQuantity = (id) => {
    const item = cartItems.find(item => item.id === id)
    return item ? item.quantity : 0
  }

  const value = {
    cartItems,
    addToCart,
    updateQuantity,
    removeFromCart,
    removeOrderItems,
    clearCart,
    getTotalItems,
    getTotalPrice,
    isInCart,
    getItemQuantity
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}
